import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Send as SendIcon } from '@mui/icons-material';
import apiService from '../services/apiService';

const TRIGGER_LABELS = {
  job_before: 'Before scheduled job',
  estimate_pending: 'Estimate pending (not accepted)',
  job_completed: 'After job completed',
};

export default function Automations() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [testRuleId, setTestRuleId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [testResult, setTestResult] = useState('');

  const [form, setForm] = useState({
    name: '',
    active: true,
    trigger_type: 'job_before',
    trigger_config: { pending_days: 3 },
    delay_minutes: 1440,
    action_channel: 'email',
    message_template_id: '',
    action_config: { subject: 'Notification from WorkWiseSoft' },
  });

  async function fetchAll() {
    setError('');
    setLoading(true);
    try {
      const [rulesRes, tplRes, metaRes] = await Promise.all([
        apiService.get('automation-rules?include_inactive=1'),
        apiService.get('message-templates?include_inactive=1'),
        apiService.get('automation-rules/meta'),
      ]);
      setItems(Array.isArray(rulesRes) ? rulesRes : []);
      setTemplates(Array.isArray(tplRes) ? tplRes : []);
      setMeta(metaRes || null);
    } catch (e) {
      setError(e.message || 'Failed to load automations');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredTemplates = templates.filter(
    (t) => t.active !== false && t.channel === form.action_channel
  );

  function openCreate() {
    setEditingId(null);
    setForm({
      name: '',
      active: true,
      trigger_type: 'job_before',
      trigger_config: { pending_days: 3 },
      delay_minutes: 1440,
      action_channel: 'email',
      message_template_id: '',
      action_config: { subject: 'Notification from WorkWiseSoft' },
    });
    setDialogOpen(true);
  }

  async function openEdit(id) {
    setError('');
    try {
      const r = await apiService.get(`automation-rules/${id}`);
      setEditingId(id);
      setForm({
        name: r.name || '',
        active: r.active !== false,
        trigger_type: r.trigger_type || 'job_before',
        trigger_config: r.trigger_config || { pending_days: 3 },
        delay_minutes: r.delay_minutes ?? 0,
        action_channel: r.action_channel === 'sms' ? 'sms' : 'email',
        message_template_id: r.message_template_id || '',
        action_config: r.action_config || { subject: 'Notification from WorkWiseSoft' },
      });
      setDialogOpen(true);
    } catch (e) {
      setError(e.message || 'Failed to load rule');
    }
  }

  async function handleSave() {
    setError('');
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        active: !!form.active,
        trigger_type: form.trigger_type,
        trigger_config:
          form.trigger_type === 'estimate_pending'
            ? { pending_days: Number(form.trigger_config?.pending_days) || 3 }
            : form.trigger_config || null,
        delay_minutes: Number(form.delay_minutes) || 0,
        action_channel: form.action_channel,
        message_template_id: form.message_template_id ? Number(form.message_template_id) : null,
        action_config: form.action_config,
      };
      if (!payload.name) {
        setError('Name is required');
        setBusy(false);
        return;
      }
      if (editingId) {
        await apiService.put(`automation-rules/${editingId}`, payload);
      } else {
        await apiService.post('automation-rules', payload);
      }
      setDialogOpen(false);
      setEditingId(null);
      await fetchAll();
    } catch (e) {
      setError(e.message || 'Failed to save rule');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this automation rule?')) return;
    setError('');
    try {
      await apiService.delete(`automation-rules/${id}`);
      await fetchAll();
    } catch (e) {
      setError(e.message || 'Failed to delete');
    }
  }

  function openTest(id) {
    setTestRuleId(id);
    setTestRecipient('');
    setTestResult('');
    setTestOpen(true);
  }

  async function handleTestSend() {
    setTestResult('');
    setBusy(true);
    try {
      const res = await apiService.post(`automation-rules/${testRuleId}/test-send`, {
        recipient: testRecipient.trim(),
        variables: {
          client_name: 'Test Client',
          job_name: 'Test job',
          scheduled_date: '2026-05-20',
          scheduled_time: '10:00',
          address: '1 Test Street',
          share_url: 'https://example.com/estimate/demo',
          estimate_id: '999',
          total: '150.00',
          currency: 'GBP',
          valid_until: '2026-06-01',
        },
      });
      setTestResult(res.message || (res.ok ? 'Sent' : 'Failed'));
    } catch (e) {
      setTestResult(e.message || 'Test send failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Automations
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none' }}>
          New rule
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Trigger + delay + email/SMS action. Runs are processed every minute via{' '}
        <code>php artisan automation:process</code> (also scheduled in production). Rescheduling a job cancels pending
        &quot;before job&quot; reminders and creates a new run.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.length === 0 ? (
            <Card>
              <CardContent>
                <Typography sx={{ fontWeight: 800 }}>No automation rules yet.</Typography>
                <Typography variant="body2" color="text.secondary">
                  Create rules for reminders before jobs, follow-ups on pending estimates, or messages after completion.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            items.map((it) => (
              <Card key={it.id} sx={{ boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900 }}>{it.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      <Chip size="small" label={TRIGGER_LABELS[it.trigger_type] || it.trigger_type} sx={{ mr: 1, height: 22 }} />
                      <Chip size="small" label={it.action_channel === 'sms' ? 'SMS' : 'Email'} sx={{ mr: 1, height: 22 }} />
                      <Chip size="small" label={`Delay ${it.delay_minutes} min`} sx={{ mr: 1, height: 22 }} />
                      {!it.active ? <Chip size="small" label="Inactive" sx={{ height: 22 }} /> : null}
                    </Typography>
                    {it.message_template ? (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Template: {it.message_template.name}
                      </Typography>
                    ) : null}
                  </Box>
                  <Button size="small" variant="outlined" startIcon={<SendIcon />} onClick={() => openTest(it.id)} sx={{ textTransform: 'none' }}>
                    Test send
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEdit(it.id)} sx={{ textTransform: 'none' }}>
                    Edit
                  </Button>
                  <IconButton aria-label="Delete" onClick={() => handleDelete(it.id)}>
                    <DeleteIcon />
                  </IconButton>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => !busy && setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>{editingId ? `Edit rule #${editingId}` : 'New automation rule'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
            <FormControl fullWidth>
              <InputLabel id="auto-trigger">Trigger</InputLabel>
              <Select
                labelId="auto-trigger"
                label="Trigger"
                value={form.trigger_type}
                onChange={(e) => setForm((f) => ({ ...f, trigger_type: e.target.value }))}
              >
                {(meta?.trigger_types || Object.keys(TRIGGER_LABELS)).map((t) => (
                  <MenuItem key={t} value={t}>
                    {TRIGGER_LABELS[t] || t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Delay (minutes)"
              type="number"
              value={form.delay_minutes}
              onChange={(e) => setForm((f) => ({ ...f, delay_minutes: e.target.value }))}
              fullWidth
              helperText="Before job: minutes before scheduled time. After completed: minutes after status change."
            />
            <FormControl fullWidth>
              <InputLabel id="auto-channel">Channel</InputLabel>
              <Select
                labelId="auto-channel"
                label="Channel"
                value={form.action_channel}
                onChange={(e) => setForm((f) => ({ ...f, action_channel: e.target.value, message_template_id: '' }))}
              >
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
              </Select>
            </FormControl>
            {form.trigger_type === 'estimate_pending' ? (
              <TextField
                label="Pending days"
                type="number"
                value={form.trigger_config?.pending_days ?? 3}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    trigger_config: { ...f.trigger_config, pending_days: Number(e.target.value) || 3 },
                  }))
                }
                fullWidth
              />
            ) : null}
            <FormControl fullWidth sx={{ gridColumn: { sm: '1 / -1' } }}>
              <InputLabel id="auto-tpl">Message template</InputLabel>
              <Select
                labelId="auto-tpl"
                label="Message template"
                value={form.message_template_id}
                onChange={(e) => setForm((f) => ({ ...f, message_template_id: e.target.value }))}
              >
                <MenuItem value="">— None —</MenuItem>
                {filteredTemplates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {form.action_channel === 'email' ? (
              <TextField
                label="Email subject"
                value={form.action_config?.subject || ''}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    action_config: { ...f.action_config, subject: e.target.value },
                  }))
                }
                fullWidth
                sx={{ gridColumn: { sm: '1 / -1' } }}
              />
            ) : null}
          </Box>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Switch checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={busy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={busy} sx={{ textTransform: 'none' }}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={testOpen} onClose={() => !busy && setTestOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Test send</DialogTitle>
        <DialogContent>
          <TextField
            label={form.action_channel === 'sms' ? 'Phone' : 'Email'}
            value={testRecipient}
            onChange={(e) => setTestRecipient(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
          />
          {testResult ? (
            <Alert severity={testResult.toLowerCase().includes('sent') ? 'success' : 'warning'} sx={{ mt: 2 }}>
              {testResult}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTestOpen(false)} disabled={busy} sx={{ textTransform: 'none' }}>
            Close
          </Button>
          <Button variant="contained" onClick={handleTestSend} disabled={busy || !testRecipient.trim()} sx={{ textTransform: 'none' }}>
            Send now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
